import React, { ReactNode } from 'react';
import { ColumnsType } from 'antd/lib/table';
import {
  DatesAtAGlanceRow,
  OptionProps,
  ReportsFilters,
  ReportsSortBy,
} from '@models/interfaces';
import { DATES_AT_A_GLANCE_MILESTONE_STATUS_LABELS } from '../dates-at-a-glance.constants';
import {
  getDatesAtAGlanceMilestoneStatus,
  getDatesAtAGlanceMilestoneStatusClassName,
} from '../dates-at-a-glance-helpers';
import {
  getDatesAtAGlanceSortableColumn,
  renderDisplayValue,
  renderValue,
} from './common';

const renderDatesStatus = (
  _value: unknown,
  record: DatesAtAGlanceRow,
): ReactNode => {
  const status = getDatesAtAGlanceMilestoneStatus(record);

  return React.createElement(
    'span',
    { className: getDatesAtAGlanceMilestoneStatusClassName(status) },
    DATES_AT_A_GLANCE_MILESTONE_STATUS_LABELS[status],
  );
};

export const getDatesAtAGlanceColumns = (
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
): ColumnsType<DatesAtAGlanceRow> => [
  {
    key: 'milestoneStatus',
    render: renderDatesStatus,
    title: 'Milestone Status',
  },
  getDatesAtAGlanceSortableColumn(
    'projectBundleID',
    'Project Bundle ID',
    ReportsSortBy.ProjectBundleID,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'fuzeIDsDisplay',
    key: 'fuzeIDsDisplay',
    render: (_value: unknown, record: DatesAtAGlanceRow): ReactNode =>
      renderDisplayValue(record.fuzeIDsDisplay),
    title: 'Fuze IDs',
  },
  {
    dataIndex: 'psProjectNumbersDisplay',
    key: 'psProjectNumbersDisplay',
    render: (_value: unknown, record: DatesAtAGlanceRow): ReactNode =>
      renderDisplayValue(record.psProjectNumbersDisplay),
    title: 'PS Project Numbers',
  },
  getDatesAtAGlanceSortableColumn(
    'taskName',
    'Milestone / Task Name',
    ReportsSortBy.TaskName,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'paceTaskID',
    key: 'paceTaskID',
    render: renderValue,
    title: 'PACE Task ID',
  },
  {
    dataIndex: 'paceTaskScreensDisplay',
    key: 'paceTaskScreensDisplay',
    render: (_value: unknown, record: DatesAtAGlanceRow): ReactNode =>
      renderDisplayValue(record.paceTaskScreensDisplay, record.paceTaskScreens),
    title: 'PACE Task Module(s)',
  },
  getDatesAtAGlanceSortableColumn(
    'projectType',
    'Project Type',
    ReportsSortBy.ProjectType,
    filters,
    sortByOptions,
    renderValue,
  ),
  getDatesAtAGlanceSortableColumn(
    'projectStatus',
    'Project Status',
    ReportsSortBy.ProjectStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getDatesAtAGlanceSortableColumn(
    'progressStatus',
    'Progress Status',
    ReportsSortBy.ProgressStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getDatesAtAGlanceSortableColumn(
    'siteName',
    'Site Name',
    ReportsSortBy.SiteName,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'psLocationCode',
    key: 'psLocationCode',
    render: renderValue,
    title: 'PS Location Code',
  },
  {
    dataIndex: 'mdgLocationCode',
    key: 'mdgLocationCode',
    render: renderValue,
    title: 'MDG Location Code',
  },
  {
    dataIndex: 'state',
    key: 'state',
    render: renderValue,
    title: 'State',
  },
  {
    dataIndex: 'submarket',
    key: 'submarket',
    render: renderValue,
    title: 'Submarket',
  },
  {
    dataIndex: 'towerOwner',
    key: 'towerOwner',
    render: renderValue,
    title: 'Tower Owner',
  },
  getDatesAtAGlanceSortableColumn(
    'forecastedDate',
    'Forecasted Date',
    ReportsSortBy.ForecastDate,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'PACEForecastedDate',
    key: 'PACEForecastedDate',
    render: renderValue,
    title: 'PACE Forecasted Date',
  },
  getDatesAtAGlanceSortableColumn(
    'actualDate',
    'Actual Date',
    ReportsSortBy.ActualDate,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'PACEActualDate',
    key: 'PACEActualDate',
    render: renderValue,
    title: 'PACE Actual Date',
  },
];

export const getDatesAtAGlanceReportRowClassName = (
  record: DatesAtAGlanceRow,
): string =>
  [
    record.projectBundleID
      ? 'prov-dates-at-a-glance-report__row--clickable'
      : 'prov-dates-at-a-glance-report__row--disabled',
  ]
    .filter(Boolean)
    .join(' ');

export const getDatesAtAGlanceReportRowKey = (
  record: DatesAtAGlanceRow,
): string =>
  [
    record.projectId,
    record.projectBundleID,
    record.paceTaskId,
    record.taskName,
    record.forecastedDate,
    record.actualDate,
  ]
    .filter(Boolean)
    .join('-');
